import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Divider, 
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { 
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as DocIcon,
  Article as TextIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
// Import PDF.js
import * as pdfjsLib from 'pdfjs-dist';
import { TextItem } from 'pdfjs-dist/types/src/display/api';

// Initialize PDF.js - use HTTPS URL
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface FileUploadComponentProps {
  onFileUpload: (file: File) => void;
  allowedFileTypes?: string[];
  maxFileSizeMB?: number;
}

const FileUploadComponent: React.FC<FileUploadComponentProps> = ({
  onFileUpload,
  allowedFileTypes = ['.pdf', '.doc', '.docx', '.txt'],
  maxFileSizeMB = 10
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processingText, setProcessingText] = useState(false);

  // Get file icon based on file type
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (extension === 'pdf') {
      return <PdfIcon color="primary" />;
    } else if (extension === 'doc' || extension === 'docx') {
      return <DocIcon color="primary" />;
    } else {
      return <TextIcon color="primary" />;
    }
  };

  // Extract text from PDF using PDF.js
  const extractTextFromPDF = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      setProcessingText(true);
      
      const fileReader = new FileReader();
      fileReader.onload = async (event) => {
        try {
          if (!event.target?.result) {
            throw new Error("Failed to read file");
          }
          
          const typedArray = new Uint8Array(event.target.result as ArrayBuffer);
          
          // Load the PDF document
          const loadingTask = pdfjsLib.getDocument({ data: typedArray });
          const pdf = await loadingTask.promise;
          console.log(`PDF loaded with ${pdf.numPages} pages`);
          
          let fullText = '';
          
          // Process each page
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            
            // Extract text from the page
            let pageText = '';
            let lastY: number | undefined = undefined;
            let lastX: number | undefined = undefined;
            
            // Process each text item
            textContent.items.forEach((item: any) => {
              const text = item.str;
              const x = item.transform[4] as number;
              const y = item.transform[5] as number;
              
              // Add newlines for significant position changes
              if (lastY !== undefined && Math.abs(y - lastY) > 5) {
                pageText += '\n';
                // Add extra newline for larger gaps
                if (Math.abs(y - lastY) > 15) {
                  pageText += '\n';
                }
              } else if (lastX !== undefined && x < lastX) {
                // New line if x position goes back (likely a new line)
                pageText += '\n';
              }
              
              pageText += text;
              lastY = y;
              lastX = x;
            });
            
            // Add page separator
            fullText += `\n--- Page ${i} ---\n\n${pageText}\n\n`;
          }
          
          setProcessingText(false);
          resolve(fullText);
        } catch (error) {
          console.error('Error extracting text from PDF:', error);
          setProcessingText(false);
          reject(error);
        }
      };
      
      fileReader.onerror = () => {
        setProcessingText(false);
        reject(new Error('Failed to read file'));
      };
      
      fileReader.readAsArrayBuffer(file);
    });
  };

  // Extract text from text file
  const extractTextFromTextFile = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsText(file);
    });
  };

  // Extract PDF as text directly (fallback method)
  const extractPdfAsText = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        if (!event.target?.result) {
          reject(new Error('Failed to read file'));
          return;
        }
        
        try {
          const arrayBuffer = event.target.result as ArrayBuffer;
          const typedArray = new Uint8Array(arrayBuffer);
          
          // Use PDF.js to extract text content
          const loadingTask = pdfjsLib.getDocument({ data: typedArray });
          const pdf = await loadingTask.promise;
          
          let extractedText = `PDF Document: ${file.name}\n\n`;
          extractedText += `Number of Pages: ${pdf.numPages}\n\n`;
          extractedText += "=== CONTENT ===\n\n";
          
          // Extract text from each page
          for (let i = 1; i <= pdf.numPages; i++) {
            try {
              const page = await pdf.getPage(i);
              const textContent = await page.getTextContent();
              
              extractedText += `--- Page ${i} ---\n\n`;
              
              // Concatenate all text items
              const pageText = textContent.items
                .map((item: any) => item.str)
                .join(' ');
                
              extractedText += pageText + '\n\n';
            } catch (pageError) {
              console.error(`Error extracting text from page ${i}:`, pageError);
              extractedText += `[Error extracting text from page ${i}]\n\n`;
            }
          }
          
          resolve(extractedText);
        } catch (error) {
          console.error('Error in PDF text extraction:', error);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  // Validate file type and size
  const validateAndSetFile = (file: File) => {
    setError(null);
    
    // Check file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedFileTypes.includes(fileExtension)) {
      setError(`Invalid file type. Allowed types: ${allowedFileTypes.join(', ')}`);
      return;
    }
    
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSizeMB) {
      setError(`File size exceeds the maximum allowed size of ${maxFileSizeMB}MB`);
      return;
    }
    
    setSelectedFile(file);
  };

  // Handle file upload button click
  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file removal
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle file upload submission
  const handleSubmit = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setProcessingText(true);
    try {
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      let extractedText = '';
      
      // Extract text based on file type
      if (fileExtension === 'pdf') {
        try {
          console.log('Extracting text from PDF...');
          
          // Try the standard extraction method first
          extractedText = await extractTextFromPDF(selectedFile);
          console.log(`Extracted ${extractedText.length} characters from PDF`);
          
          // If little or no text was extracted, try the fallback method
          if (!extractedText || extractedText.length < 100) {
            console.log('Limited text extracted. Trying fallback method...');
            extractedText = await extractPdfAsText(selectedFile);
            console.log(`Fallback extraction: ${extractedText.length} characters`);
          }
          
          // If still no text, provide a placeholder
          if (!extractedText || extractedText.length < 50) {
            console.warn('Very little or no text extracted from PDF');
            extractedText = `PDF Document: ${selectedFile.name}\n\nThis appears to be a scanned or image-based PDF. The text content could not be automatically extracted. Please use the document editor to manually enter or edit the content.`;
          }
        } catch (err) {
          console.error('Error extracting text from PDF:', err);
          // Don't return early - allow the PDF to be uploaded even if text extraction fails
          extractedText = `PDF Document: ${selectedFile.name}\n\nFailed to extract text from this PDF. The document may be scanned, image-based, or protected. Please use the document editor to manually enter or edit the content.`;
        }
      } else if (fileExtension === 'txt') {
        extractedText = await extractTextFromTextFile(selectedFile);
      } else {
        // For other file types, we'd need specific extractors
        extractedText = `File content extraction not supported for ${fileExtension} files yet.`;
      }
      
      // Store the extracted text in localStorage for other components to use
      if (extractedText) {
        console.log('Storing extracted text in localStorage');
        localStorage.setItem('extractedDocumentText', extractedText);
      }
      
      // Store the file type in localStorage to help other components decide how to handle it
      localStorage.setItem('uploadedFileType', selectedFile.type);
      
      // Call the onFileUpload callback
      onFileUpload(selectedFile);
      
    } catch (error) {
      console.error('Error processing file:', error);
      setError('Failed to process file. Please try again.');
    } finally {
      setUploading(false);
      setProcessingText(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* File upload area */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: dragActive ? '2px dashed #2196f3' : '2px dashed #e0e0e0',
          backgroundColor: dragActive ? 'rgba(33, 150, 243, 0.04)' : 'background.paper',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          textAlign: 'center',
          mb: 3
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleUploadButtonClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={allowedFileTypes.join(',')}
          style={{ display: 'none' }}
        />
        
        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          Drag & Drop your contract file here
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          or click to browse files
        </Typography>
        
        <Typography variant="caption" color="text.secondary">
          Supported formats: {allowedFileTypes.join(', ')} (Max size: {maxFileSizeMB}MB)
        </Typography>
      </Paper>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Selected file preview */}
      {selectedFile && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <List disablePadding>
            <ListItem
              secondaryAction={
                <IconButton edge="end" onClick={handleRemoveFile} disabled={uploading || processingText}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemIcon>
                {getFileIcon(selectedFile.name)}
              </ListItemIcon>
              <ListItemText
                primary={selectedFile.name}
                secondary={`${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`}
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={uploading || processingText}
              startIcon={
                (uploading || processingText) ? 
                <CircularProgress size={20} color="inherit" /> : 
                null
              }
            >
              {uploading ? 'Uploading...' : 
               processingText ? 'Processing Text...' : 
               'Process Document'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Alternative options */}
      {!selectedFile && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Or start with a template
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => navigate('/templates')}
            sx={{ mt: 1 }}
          >
            Browse Templates
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FileUploadComponent;
